import LayoutContainer from 'components/Common/LayoutContainer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Sheet from 'components/utils/Sheet'
import useDetectCarouselSlide from 'hooks/useDetectCarouselSlide'
import Carousel from 'LuxKit/Carousel/Carousel'
import { getElementLogicalInlineStartOffset, scrollToLogicalPosition } from 'LuxKit/Carousel/utils'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ElementType } from 'react'
import styled from 'styled-components'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import Group from 'components/utils/Group'

const Grid = styled.div`
  display: grid;
  gap: ${rem(28)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(var(--cars-value-proposition-tablet-columns, 1), minmax(0, 1fr));
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(var(--cars-value-proposition-desktop-columns, 1), minmax(0, 1fr));
    gap: ${rem(24)};
  }
`

const Item = styled.div`
  display: grid;
  gap: ${rem(12)};
`

const Copy = styled.div`
  display: grid;
  gap: ${rem(4)};
`

export interface CarsValueProposition {
  id: string
  icon: ElementType
  title: string
  description: string
}

interface Props {
  values: Array<CarsValueProposition>
}

interface ValuePropositionItemProps {
  value: CarsValueProposition
}

function ValuePropositionItem(props: ValuePropositionItemProps) {
  const { value } = props
  const Icon = value.icon

  return <Item>
    <Icon size="L" colour="highlight-primary-dark" />
    <Copy>
      <BodyText variant="medium" weight="bold">
        {value.title}
      </BodyText>
      <BodyText variant="medium">
        {value.description}
      </BodyText>
    </Copy>
  </Item>
}

function CarsValuePropositions(props: Props) {
  const { values } = props

  const carouselRef = useRef<HTMLDivElement | null>(null)
  const slideElements = useRef<Array<HTMLDivElement | null>>([])

  const [activeIndex, setActiveIndex] = useState(0)

  const gridStyle = useMemo(() => ({
    '--cars-value-proposition-tablet-columns': Math.min(values.length, 2),
    '--cars-value-proposition-desktop-columns': Math.min(values.length, 4),
  } as CSSProperties), [values.length])

  const onScroll = useDetectCarouselSlide(
    carouselRef,
    slideElements,
    setActiveIndex,
    'start',
  )

  const onSlideSelect = useCallback((index: number) => {
    setActiveIndex(index)

    const carousel = carouselRef.current
    const slideElement = slideElements.current[index]

    if (carousel && slideElement) {
      scrollToLogicalPosition(carousel, {
        left: getElementLogicalInlineStartOffset(slideElement, carousel),
        behavior: 'smooth',
      })
    }
  }, [])

  if (!values.length) return null

  return <Sheet background="light-background" padding="12 0 40 0" tabletPadding="32 0">
    <LayoutContainer>
      <CSSBreakpoint max="mobile">
        <Carousel
          ref={carouselRef}
          onScroll={onScroll}
          gap={16}
          snap="start"
          width={rem(260)}
          gutterSize={24}
        >
          {values.map((value, index) => (
            <div
              key={value.id}
              ref={(element) => {
                slideElements.current[index] = element
              }}
            >
              <ValuePropositionItem value={value} />
            </div>
          ))}
        </Carousel>
        <Sheet padding="16 0 0 0">
          <Group direction="horizontal" horizontalAlign="start">
            <PaginationDots
              activeIndex={activeIndex}
              count={values.length}
              onSelect={onSlideSelect}
              variant="outlined"
            />
          </Group>
        </Sheet>
      </CSSBreakpoint>
      <CSSBreakpoint min="tablet">
        <Grid style={gridStyle}>
          {values.map(value => (
            <ValuePropositionItem key={value.id} value={value} />
          ))}
        </Grid>
      </CSSBreakpoint>
    </LayoutContainer>
  </Sheet>
}

export default CarsValuePropositions
